import { useEffect, useState } from 'react';

/**
 * Tracks a few common viewport breakpoints with matchMedia so pages can render
 * different layouts (icon-only buttons, card lists, ...). SSR-safe — defaults
 * to "desktop" when window is unavailable.
 *
 * Breakpoints (max-width):
 *   - tiny    ≤ 480px (phones in portrait)
 *   - mobile  ≤ 640px (phones / small tablets)
 *   - tablet  ≤ 900px
 */
const QUERIES = {
  isTiny: '(max-width: 480px)',
  isMobile: '(max-width: 640px)',
  isTablet: '(max-width: 900px)'
};

const readSnapshot = () => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return { isTiny: false, isMobile: false, isTablet: false };
  }
  return Object.fromEntries(
    Object.entries(QUERIES).map(([k, q]) => [k, window.matchMedia(q).matches])
  );
};

const useResponsive = () => {
  const [state, setState] = useState(readSnapshot);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mqls = Object.entries(QUERIES).map(([k, q]) => [k, window.matchMedia(q)]);
    const update = () => setState(readSnapshot());
    mqls.forEach(([, mql]) => mql.addEventListener?.('change', update));
    return () => {
      mqls.forEach(([, mql]) => mql.removeEventListener?.('change', update));
    };
  }, []);

  return state;
};

export default useResponsive;
